import {
    useContext,
    useEffect,
    useMemo,
    useRef,
    type FC
} from "react";
import type IChartRangeProps from "./type";
import type {
    ChartRangeItem
} from "./type";
import {
    NCoreUIKitLocalize
} from "../../../../core/hooks";
import type {
    ISelectBoxRef
} from "../../../selectBox/type";
import {
    ChartContext
} from "../../context";
import ToggleGroup from "../../../toggleGroup";
import SelectBox from "../../../selectBox";

export const CHART_RANGE_SEGMENT_LIMIT = 4;

const ChartRange: FC<IChartRangeProps> = ({
    customLocalize: customLocalizeProp,
    customTheme: customThemeProp,
    title: titleProp,
    isDisabled,
    onChange,
    options,
    value,
    style
}) => {
    const context = useContext(ChartContext);

    const customTheme = customThemeProp ?? context?.customTheme;

    const customLocalize = customLocalizeProp ?? context?.customLocalize;

    const {
        localize
    } = NCoreUIKitLocalize.useContext(customLocalize);

    const selectRef = useRef<ISelectBoxRef<ChartRangeItem>>(null);

    const title = titleProp ?? localize("chart-range");

    const isSegmented = options.length <= CHART_RANGE_SEGMENT_LIMIT;

    const data = useMemo(() => options.map((option) => ({
        __title: option.title,
        __key: option.key
    })), [
        options
    ]);

    const selectedItem = data.find((item) => item.__key === value) ?? data[0];

    useEffect(() => {
        if(isSegmented || !selectedItem) {
            return;
        }

        selectRef.current?.updateSelections([
            selectedItem
        ]);
    }, [
        selectedItem?.__key,
        isSegmented
    ]);

    const handleChange = (nextValue: string | Array<string>) => {
        const nextKey = Array.isArray(nextValue) ? nextValue[0] : nextValue;

        if(nextKey && nextKey !== value) {
            onChange(nextKey);
        }
    };

    if(isSegmented) {
        return <ToggleGroup
            onValueChange={handleChange}
            attachBehaviour="attached"
            customTheme={customTheme}
            selectBehaviour="single"
            isDisabled={isDisabled}
            isClearEnabled={false}
            variant="outline"
            style={style}
            title={title}
            value={value}
            size="small"
        >
            {
                options.map((option) => <ToggleGroup.Item
                    title={option.title}
                    value={option.key}
                    key={option.key}
                />)
            }
        </ToggleGroup>;
    }

    return <SelectBox<ChartRangeItem>
        onChange={(selectedItems) => {
            const nextKey = selectedItems[0]?.__key;

            if(nextKey) {
                handleChange(nextKey);
            }
        }}
        titleExtractor={(item) => item.__title}
        keyExtractor={(item) => item.__key}
        defaultValue={selectedItem?.__key}
        customLocalize={customLocalize}
        isWorkWithRealtime={true}
        customTheme={customTheme}
        isDisabled={isDisabled}
        isClearEnabled={false}
        spreadBehaviour="free"
        placeholder={title}
        ref={selectRef}
        style={style}
        size="small"
        data={data}
    />;
};
export default ChartRange;
